import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Home.css';

function Home() {
  const images = [
    '/images/Text_Screen1.png',
    '/images/Text_Screen2.png',
    '/images/Text_Screen3.png'
  ];
  const loopedImages = [images[images.length - 1], ...images, images[0]];
  
  // Start at index 1 for the first "real" slide.
  const [currentIndex, setCurrentIndex] = useState(1);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  // Track whether a transition is ongoing.
  const [isAnimating, setIsAnimating] = useState(false);
  
  const slideInterval = useRef(null);
  const sliderRef = useRef(null);
  const startX = useRef(null);
  const endX = useRef(null);
  
  const stopAutoSlide = useCallback(() => {
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
    }
  }, []);
  
  const startAutoSlide = useCallback(() => {
    stopAutoSlide();
    slideInterval.current = setInterval(() => {
      if (!isAnimating) {
        setCurrentIndex(prev => prev + 1);
        setIsAnimating(true);
      }
    }, 5000);
  }, [stopAutoSlide, isAnimating]);
  
  useEffect(() => {
    startAutoSlide();
    return () => stopAutoSlide();
  }, [startAutoSlide, stopAutoSlide]);
  
  // Handle transition end: jump if needed and re-enable transitions.
  const handleTransitionEnd = () => {
    // End of a transition; allow new swipes.
    setIsAnimating(false);
    
    if (currentIndex === 0) {
      setTransitionEnabled(false);
      requestAnimationFrame(() => {
        setCurrentIndex(images.length);
        requestAnimationFrame(() => setTransitionEnabled(true));
      });
    }
    if (currentIndex === loopedImages.length - 1) {
      setTransitionEnabled(false);
      requestAnimationFrame(() => {
        setCurrentIndex(1);
        requestAnimationFrame(() => setTransitionEnabled(true));
      });
    }
  };
  
  const goToSlide = (index) => {
    if (isAnimating) return;
    setCurrentIndex(index + 1);
    setIsAnimating(true);
  };
  
  // Touch event handlers
  const handleTouchStart = (e) => {
    if (isAnimating) return;
    startX.current = e.touches[0].clientX;
  };
  
  const handleTouchMove = (e) => {
    if (isAnimating) return;
    endX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = () => {
    if (isAnimating) return;
    if (startX.current === null || endX.current === null) return;
    
    const distance = startX.current - endX.current;
    const threshold = 50;
    if (distance > threshold) {
      setCurrentIndex(prev => prev + 1);
      setIsAnimating(true);
    } else if (distance < -threshold) {
      setCurrentIndex(prev => prev - 1);
      setIsAnimating(true);
    }
    startX.current = null;
    endX.current = null;
  };
  
  // Mouse event handlers
  const handleMouseDown = (e) => {
    if (isAnimating) return;
    startX.current = e.clientX;
  };
  
  const handleMouseMove = (e) => {
    if (isAnimating || startX.current === null) return;
    endX.current = e.clientX;
  };
  
  const handleMouseUp = () => {
    if (isAnimating) return;
    if (startX.current === null || endX.current === null) return;
    
    const distance = startX.current - endX.current;
    const threshold = 50;
    if (distance > threshold) {
      setCurrentIndex(prev => prev + 1);
      setIsAnimating(true);
    } else if (distance < -threshold) {
      setCurrentIndex(prev => prev - 1);
      setIsAnimating(true);
    }
    startX.current = null;
    endX.current = null;
  };

  return (
    <div className="home-container">
      <div 
        className="slideshow-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          ref={sliderRef}
          className="slider"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            transition: transitionEnabled ? 'transform 0.5s ease' : 'none'
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {loopedImages.map((image, index) => (
            <div key={index} className="slide">
              <img src={image} alt={`Airrived AI – Agentic Cybersecurity Platform`} className="slideshow-image" />
            </div>
          ))}
        </div>
        <div className="dots-container">
          {images.map((_, index) => (
            <span 
              key={index}
              className={`dot ${currentIndex === index + 1 ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            ></span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Home;